/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import classNames from 'classnames';

import { RootState } from '../ducks/store';
import { isPlatformAdmin, isTeamManager } from '../utils/functions';
import FeedbackModal from './common/FeedbackModal/FeedbackModal';
import { ImageURLs, ModalTypes } from '../utils/constants';
import { showModal } from '../ducks/modal';

import './TopNav.scss';

const win: IWindow = window;

interface ITopNavBrandingPreviewWrapperProps {
  children?: React.ReactNode;
  className?: string;
}

export const TopNavBrandingPreviewWrapper = ({
  children,
  className,
}: ITopNavBrandingPreviewWrapperProps) => (
  <div className={classNames('TopNavBrandingPreviewWrapper', className)}>{children}</div>
);

interface TopNavBrandingIProps {
  collaborationLogo?: string;
  teamName?: string;
  useWhiteFilter?: boolean;
  className?: string;
  isPreview?: boolean;
}

export const TopNavBranding = ({
  collaborationLogo,
  teamName,
  useWhiteFilter,
  className,
  isPreview,
}: TopNavBrandingIProps) => (
  <div className={classNames('TopNavBranding', className, { isPreview })}>
    <img
      className="TopNavBranding-praktio-logo"
      src={ImageURLs.praktioLogoWhite}
      alt="Praktio Logo"
    />
    {collaborationLogo && (
      <div
        className={classNames(
          'TopNavBranding-collaborator h-100 justify-content-center align-items-center',
          { 'd-none d-lg-flex': !isPreview, 'd-flex': isPreview },
        )}
      >
        <div>@</div>
        <img
          className={classNames('TopNavBranding-collaborator-logo', {
            'white-filter': useWhiteFilter,
          })}
          src={collaborationLogo}
          alt={`${teamName} Logo`}
        />
      </div>
    )}
  </div>
);

interface ILinkToggleProps {
  active?: boolean;
  onClick: (e?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

const LinkToggle = React.forwardRef<{}, ILinkToggleProps>(({ children, active, onClick }, ref) => (
  <a
    className={classNames('is-link', { active })}
    href=""
    ref={ref as any}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
  >
    {children}
  </a>
));

interface ISubMenuProps {
  pathname?: string;
  settingsPath: string;
  isAdmin: boolean;
  isManager: boolean;
  navigate: NavigateFunction;
}

const SubMenu: FC<ISubMenuProps> = ({ pathname, settingsPath, isAdmin, isManager, navigate }) => {
  const dispatch = useDispatch();
  const [showContactMenu, setShowContactMenu] = useState(false);
  const [showReportMenu, setShowReportMenu] = useState(false);
  const [subMenuShowing, setSubMenuShowing] = useState(false);
  return (
    <Dropdown
      className="TopNav-submenu"
      onToggle={(show) => {
        if (!show) {
          setShowContactMenu(false);
          setShowReportMenu(false);
        }

        setSubMenuShowing(show);
      }}
    >
      <Dropdown.Toggle id="submenu" as={LinkToggle}>
        <span className="material-icons">{subMenuShowing ? 'close' : 'menu'}</span>
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {isAdmin && (
          <>
            <Dropdown.Item
              className={classNames('is-link', { active: pathname === Paths.Courses })}
              onClick={() => navigate(Paths.Courses)}
            >
              COURSES
            </Dropdown.Item>
            <Dropdown.Item
              className={classNames('is-link', { active: pathname === Paths.Teams })}
              onClick={() => navigate(Paths.Teams)}
            >
              TEAMS
            </Dropdown.Item>
            <Dropdown.Item
              className={classNames('is-link', { active: pathname === Paths.Users })}
              onClick={() => navigate(Paths.Users)}
            >
              USERS
            </Dropdown.Item>
            <div
              className={classNames('report-submenu', {
                active: [Paths.Reports, Paths.GeneralReport, Paths.CLEReport].includes(
                  pathname || '',
                ),
              })}
              onClick={() => setShowReportMenu(!showReportMenu)}
            >
              REPORTS
            </div>
            {showReportMenu && (
              <>
                <Dropdown.Item
                  className={classNames('submenu-link')}
                  onClick={() => navigate(Paths.GeneralReport)}
                >
                  General
                </Dropdown.Item>
                <Dropdown.Item
                  className={classNames('submenu-link')}
                  onClick={() => navigate(Paths.CLEReport)}
                >
                  CLE
                </Dropdown.Item>
              </>
            )}
            <Dropdown.Divider />
          </>
        )}
        {isManager && !isAdmin && (
          <>
            <Dropdown.Item
              className={classNames('is-link', { active: pathname === Paths.Reports })}
              onClick={() => navigate(Paths.Reports)}
            >
              REPORTS
            </Dropdown.Item>
            <Dropdown.Divider />
          </>
        )}
        <div
          className={classNames('contact-submenu')}
          onClick={() => setShowContactMenu(!showContactMenu)}
        >
          CONTACT
        </div>
        {showContactMenu && (
          <>
            <Dropdown.Item
              className={classNames('submenu-link')}
              onClick={() => {
                if (win.HubSpotConversations) {
                  win.HubSpotConversations.widget.open();
                }
              }}
            >
              <span className="material-icons">chat_bubble_outline</span>Start a Chat
            </Dropdown.Item>
            <Dropdown.Item
              className={classNames('submenu-link')}
              onClick={() => dispatch(showModal(ModalTypes.Feedback))}
              data-cy="mail"
            >
              <span className="material-icons">mail_outline</span>Send a Message
            </Dropdown.Item>
          </>
        )}
        <Dropdown.Item
          className={classNames('is-link', { active: settingsPath === pathname })}
          onClick={() => navigate(settingsPath)}
        >
          SETTINGS
        </Dropdown.Item>
        <a
          className={classNames('dropdown-item', 'is-link')}
          href={`${process.env.REACT_APP_API || '/api/v1/'}tenant/praktio/common/auth/logout`}
        >
          LOG OUT
        </a>
      </Dropdown.Menu>
    </Dropdown>
  );
};

const Paths = {
  Dashboard: '/',
  Courses: '/admin/courses',
  Teams: '/admin/teams',
  Users: '/admin/users',
  AdminSettings: '/admin/settings',
  Settings: '/settings',
  Reports: '/manager/reports',
  GeneralReport: '/admin/reports/general',
  CLEReport: '/admin/reports/cle',
};

const UnauthedPaths = {
  ResetPassword: '/reset',
  ForgotPassword: '/forgot',
  SelfSignup: '/signup',
  Register: '/register',
};

const TopNav = () => {
  const { authenticatedUser, isAdmin, isManager, modalType } = useSelector((state: RootState) => {
    const { authenticatedUser } = state.auth;
    const { showType } = state.modal;
    return {
      authenticatedUser,
      isAdmin: isPlatformAdmin(authenticatedUser),
      isManager: isTeamManager(authenticatedUser),
      modalType: showType,
    };
  });
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const pathname = location?.pathname;
  const unauthedPath = Object.values(UnauthedPaths).includes(pathname);
  if (!authenticatedUser || unauthedPath) {
    return null;
  }

  const settingsPath = isAdmin ? Paths.AdminSettings : Paths.Settings;
  return (
    <>
      <div className="TopNav">
        <div className="TopNav-left">
          <Link
            className={classNames('is-link', 'dashboard-icon', {
              active: pathname === Paths.Dashboard,
            })}
            to={Paths.Dashboard}
            aria-label="Home"
          >
            <span className="material-icons" aria-hidden="true">
              grid_view
            </span>
          </Link>
          {isAdmin && (
            <>
              <Link
                className={classNames('is-link', 'sm-hide', { active: pathname === Paths.Courses })}
                to={Paths.Courses}
              >
                COURSES
              </Link>
              <Link
                className={classNames('is-link', 'sm-hide', { active: pathname === Paths.Teams })}
                to={Paths.Teams}
              >
                TEAMS
              </Link>
              <Link
                className={classNames('is-link', 'sm-hide', { active: pathname === Paths.Users })}
                to={Paths.Users}
              >
                USERS
              </Link>
              <Dropdown className="TopNav-dropdown sm-hide">
                <Dropdown.Toggle
                  id="reports"
                  as={LinkToggle}
                  active={[Paths.Reports, Paths.GeneralReport, Paths.CLEReport].includes(
                    pathname || '',
                  )}
                >
                  REPORTS
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => navigate(Paths.GeneralReport)}>
                    General
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => navigate(Paths.CLEReport)}>CLE</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </>
          )}
          {isManager && !isAdmin && (
            <Link
              className={classNames('is-link', 'sm-hide', { active: pathname === Paths.Reports })}
              to={Paths.Reports}
            >
              REPORTS
            </Link>
          )}
        </div>
        <div className="TopNav-center">
          <Link to="/" aria-label="Home">
            <TopNavBranding
              collaborationLogo={authenticatedUser?.teamLogo?.logo}
              teamName={authenticatedUser?.teamLogo?.teamName}
              useWhiteFilter={authenticatedUser?.teamLogo?.useWhiteFilter}
            />
          </Link>
        </div>
        <div className="TopNav-right">
          <Dropdown className="TopNav-dropdown sm-hide" id="contact-dropdown">
            <Dropdown.Toggle id="contact" as={LinkToggle}>
              CONTACT
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => {
                  if (win.HubSpotConversations) {
                    win.HubSpotConversations.widget.open();
                  }
                }}
              >
                <span className="material-icons">chat_bubble_outline</span>Start a Chat
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => dispatch(showModal(ModalTypes.Feedback))}
                data-cy="mail"
              >
                <span className="material-icons">mail_outline</span>Send a Message
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Link
            className={classNames('is-link', 'sm-hide', { active: settingsPath === pathname })}
            to={settingsPath}
          >
            SETTINGS
          </Link>
          <a
            className={classNames('is-link', 'sm-hide')}
            href={`${process.env.REACT_APP_API || '/api/v1/'}tenant/praktio/common/auth/logout`}
          >
            LOG OUT
          </a>
          <SubMenu
            pathname={pathname}
            settingsPath={settingsPath}
            isAdmin={isAdmin}
            isManager={isManager}
            navigate={navigate}
          />
        </div>
      </div>
      <FeedbackModal
        show={modalType === ModalTypes.Feedback}
        onHide={() => dispatch(showModal())}
      />
    </>
  );
};

export default TopNav;
